import { Box, Tooltip } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Delete } from "@mui/icons-material";
import GenericCrudTableModal from "@/components/tables/genericCrudTableModal";
import DropDown from "@/components/forms/inputs/dropDown";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { IDropDownOption } from "@/types/DropDown";
import GenericDateTime from "@/components/dateTime/genericDateTime";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { DropDownSubjectSearch } from "@/components/forms/inputs/dropDownSubjectSearch";
import { api } from "@/helper/api";
import { MaterialReactTable } from "material-react-table";
import MrtToolbarInternalActions from "@/components/tables/mrtToolbarInternalActions";
import {
  hasMrtFiltersToReset,
  resetMrtFilters,
} from "@/components/tables/mrtTableReset.utils";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import RestrictedPage from "@/consumer/restrictedPage";
import AppointmentBuildingInformation from "@/components/appointment/appointmentBuildingInformation";
import AppointmentCancellationChoiceDialog from "@/components/appointment/appointmentCancellationChoiceDialog";
import styles from "@/routes/admin/appointmentEditor.module.css";
import { getTranslatedOptionObject } from "@/helper/statusOptions";
import {
  DEFAULT_KAER_TEAM_ID,
  KAER_COMPANY_SUBJECT_ID,
} from "@/helper/systemIds";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useSelector } from "react-redux";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { RootState } from "@/components/state/store";

interface IAssignedCategoriesExaminations {
  appointment?: string;
  category?: string;
  category_name?: string;
  examination?: string;
  examination_name?: string;
  findngs?: [];
  id?: string;
  next_version?: number | undefined;
  status?: string;
  trace_id?: string;
  version?: number;
}

interface IMainCatList {
  created_by?: string;
  created_on?: string;
  description?: string;
  id?: string;
  modified_by?: string;
  modified_on?: string;
  name?: string;
  next_version?: number;
  trace_id?: string;
  version?: number;
}

interface IExaminationList extends IMainCatList {
  category_set?: string[];
}

export function AppointmentEditor() {
  // const [assignedCategoriesExaminations, setAssignedCategoriesExaminations] =
  //   useState<IAssignedCategoriesExaminations[] | any>([]);

  const [assignedFindings, setAssignedFindings] = useState<
    IAssignedCategoriesExaminations[] | any
  >([]);
  /*eslint-disable */
  const [mainCategoryList, setMainCategoryList] = useState<IMainCatList[]>([]);
  const [examinationList, setExaminationList] = useState<IExaminationList>({});
  const [appointmentStatus, setAppointmentStatus] = useState<IDropDownOption>();
  const [initialAppointmentStatus, setInitialAppointmentStatus] =
    useState<string>("");
  const [date, setDate] = useState<any>("");

  const [subject, setSubject] = useState<any>([]);
  const [subjectOptions, setSubjectOptions] = useState<IDropDownOption[]>([]);
  /*eslint-enable */
  const [team, setTeam] = useState<any>("");
  const [teamOptions, setTeamOptions] = useState<IDropDownOption[]>([]);
  const [executedBy, setExecutedBy] = useState<any>("");
  const [onSite, setOnsite] = useState<boolean | null>(null);

  const [executedByOptions, setExecutedByOptions] = useState<any>([]);
  const [assistedBy, setAssistedBy] = useState<any>("");
  const [assistedByOptions, setAssistedByOptions] = useState<any>([]);

  const [, setModalMode] = useState<string>("insert");
  const [modalPresetValues, setModalPresetValues] = useState<any>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  // eslint-disable-next-line
  const [rowToUpdate, setRowToUpdate] = useState<any>([]);
  const { handleError, errorModal } = useErrorModal();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [buildingInformation, setBuildingInformation] = useState<any>();
  const [, setIsCustomBuildingInfo] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState<any>();
  const [pendingCancellation, setPendingCancellation] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [savingAppointment, setSavingAppointment] = useState(false);
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [, setCustomerInformation] = useState<string>("");

  const [formValues, setFormValues] = useState<any>({
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    parking_situation: "",
    additional_information: "",
    customer_information: "",
  });
  const [buildingOptionsForSubject, setBuildingOptionsForSubject] =
    useState<any>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<any>();
  // eslint-disable-next-line
  const [
    isAssignedExaminationTableLoading,
    setIsAssignedExaminationTableLoading,
  ] = useState<boolean>(false);
  const [assignedExaminationSet, setAssignedExaminationSet] = useState<any>([]);
  const [assignedCategory, setAssignedCategory] = useState<any>(null);
  const [examinationTableData, setExaminationTableData] = useState<any>([]);

  const [allOptionsLoaded, setAllOptionsLoaded] = useState<any>({
    subjectOptions: true,
    teamOptions: false,
    executedByOptions: false,
    assistedByOptions: false,
    onSite: false,
    buildingInformation: false,
    buildingInformationOptions: false,
  });

  const { appointmentId } = useParams() as { appointmentId: string };
  const navigate = useNavigate();
  dayjs.locale("de");

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  const { showConfirmationModalAsync } = useConfirmationModal();

  const { t } = useTranslation();
  usePageHeading(t("appointment_edit"));

  // const statusOptions = getTranslatedStatusOptions();
  const logedInUserInfo: any = useSelector((state: RootState) => state.login);

  const onSiteOptions = [
    { label: t("yes"), value: true },
    { label: t("no"), value: false },
  ];

  useEffect(() => {
    getAppointmentStatusOptions();
  }, []);

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  const resetTable = useCallback(() => {
    setIsAssignedExaminationTableLoading(true);
    api.genericApiRequest({
      entity: "appointment",
      method: "get",

      successHandler: (res: any) => {
        const appointment = res.data;
        setAssignedCategory(appointment?.category);
        setAssignedExaminationSet(appointment?.examination_set);

        setAssignedFindings(appointment.finding_set);
        setIsAssignedExaminationTableLoading(false);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: String(appointmentId),
      parametersToRender: { depth: "1" },
    });
  }, [appointmentId]);
  const getAppointment = async () => {
    try {
      const res: any = await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointment",
          method: "get",
          entityId: String(appointmentId),
          parametersToRender: { depth: "1" },
          successHandler: (res: any) => {
            resolve(res);
          },
          failHandler: (error: any) => {
            reject(error);
          },
        });
      });
      const appointment = res.data;
      setAssignedCategory(appointment?.category);
      setAssignedExaminationSet(appointment?.examination_set);
      setAssignedFindings(appointment.finding_set);
      setCurrentAppointment(res.data);
      setDate(dayjs(appointment.datetime));
      setAppointmentStatus(appointment.status);
      const statusValue =
        typeof appointment.status === "object"
          ? appointment.status.value
          : appointment.status;
      setInitialAppointmentStatus(statusValue);
      setSubject({
        value: appointment.subject?.id,
        label: appointment.subject?.name,
        companySubjectId: appointment.subject?.company?.subject_id
          ? appointment.subject?.company?.subject_id
          : appointment.subject?.id,
      });
      setTeam(appointment.team?.id);
      setExecutedBy(appointment.executed_by?.id);
      setAssistedBy(appointment.assisted_by?.id);
      setBuildingInformation(appointment.building_information);
      setOnsite(appointment?.on_site);
      setAllOptionsLoaded((prevOptions: any) => ({
        ...prevOptions,
        onSite: true,
      }));

      setCustomerInformation(
        appointment?.building_information?.customer_information || ""
      );
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    getAppointment();

    api.genericApiRequest({
      entity: "team",
      method: "get",

      successHandler: (res: any) => {
        const teams = [];
        for (const key in res.data.results) {
          const item = res.data.results[key];
          const allTeams = { value: item.id, label: item.name };
          teams.push(allTeams);
        }
        setTeamOptions(teams);
        setAllOptionsLoaded((prevOptions: any) => ({
          ...prevOptions,
          teamOptions: true,
        }));
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
    const additionalUserParameters: any = {};
    additionalUserParameters["impersonate_subject"] = KAER_COMPANY_SUBJECT_ID;
    additionalUserParameters["is_active"] = "true";

    api.genericApiRequest({
      entity: "user",
      method: "get",
      parametersToRender: {
        additionalUrlParameters: additionalUserParameters,
        depth: "0",
      },

      successHandler: (res: any) => {
        const tempUsers = [];
        for (const key in res.data.results) {
          const item = res.data.results[key];
          const allUsers = {
            value: item.subject_id,
            label: item.last_name + ", " + item.first_name,
          };
          tempUsers.push(allUsers);
        }
        setExecutedByOptions(
          tempUsers.sort((a: any, b: any) => a.label.localeCompare(b.label))
        );
        setAssistedByOptions(
          tempUsers.sort((a: any, b: any) => a.label.localeCompare(b.label))
        );
        setAllOptionsLoaded((prevOptions: any) => ({
          ...prevOptions,
          executedByOptions: true,
          assistedByOptions: true,
        }));
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  }, []);

  useEffect(() => {
    if (subject.companySubjectId !== undefined) {
      const additionalUrlParameters: any = {};
      additionalUrlParameters["subject_id"] = subject.companySubjectId;
      api.genericApiRequest({
        entity: "building",
        method: "get",
        parametersToRender: {
          depth: "0",
          additionalUrlParameters: additionalUrlParameters,
        },
        successHandler: (res: any) => {
          const data = res.data.results;
          const tempBuildingOptions = [];
          for (const key in data) {
            const building = data[key];
            const value = building.id;
            const label =
              building.name +
              ` (${building?.street_house}, ${building?.zip} ${building?.city} )`;
            tempBuildingOptions.push({
              value: value,
              label: label,
              contact_name: building.contact_name,
              contact_email: building.contact_email,
              contact_phone: building.contact_phone,
              parking_situation: building.parking_situation,
              additional_information: building.additional_information,
              customer_information: building.customer_information,
            });
          }
          setBuildingOptionsForSubject(tempBuildingOptions);
          setAllOptionsLoaded((prevOptions: any) => ({
            ...prevOptions,
            buildingInformationOptions: true,
          }));
        },
        failHandler: (error: any) => {
          handleError(error);
        },
      });
    }
  }, [subject.value]);

  useEffect(() => {
    if (buildingInformation) {
      setSelectedBuilding(buildingInformation?.building?.id);
      setFormValues({
        contact_name: buildingInformation.contact_name ?? "",
        contact_phone: buildingInformation.contact_phone ?? "",
        contact_email: buildingInformation.contact_email ?? "",
        parking_situation: buildingInformation.parking_situation ?? "",
        additional_information:
          buildingInformation.additional_information ?? "",
        customer_information: buildingInformation.customer_information ?? "",
      });
      setAllOptionsLoaded((prevOptions: any) => ({
        ...prevOptions,
        buildingInformation: true,
      }));
    } else {
      setAllOptionsLoaded((prevOptions: any) => ({
        ...prevOptions,
        buildingInformation: true,
      }));
    }
  }, [buildingInformation]);

  useEffect(() => {
    if (assignedExaminationSet && assignedCategory) {
      const transformedData = assignedExaminationSet.map((exam: any) => ({
        ...exam,
        category: {
          id: assignedCategory.id,
          name: assignedCategory.name,
        },
      }));
      setExaminationTableData(transformedData);
    } else {
      setExaminationTableData([]);
    }
  }, [assignedExaminationSet, assignedCategory]);

  const navigateToAppointmentlist = async () => {
    const isConfirmed = await showConfirmationModalAsync(t("confirm_go_back"));
    if (!isConfirmed) return;

    navigate(`/appointmentlist/`);
  };

  const handleTeamChange = (selectedOption: any) => {
    setTeam(selectedOption);
  };

  const handleStatusChange = (selectedOption: any) => {
    setAppointmentStatus(selectedOption);
  };

  const handleSubjectChange = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSubject(selectedOption);
    }
  };

  const handleDateChange = (newDate: any) => {
    setDate(newDate);
  };
  const openCreateModal = () => {
    setModalMode("insert");
    setModalPresetValues({
      status: "open",
      team: DEFAULT_KAER_TEAM_ID,
      category: assignedCategory?.id,
    });
    setModalOpen(true);
  };
  // editfunctionality currently disabled

  // const openEditModal = (row: any) => {
  //   setModalMode("edit");
  //   setModalPresetValues({
  //     status: "open",
  //     category: row.original.category,
  //     examination: row.original.examination,
  //   });
  //   setModalOpen(true);
  //   setRowToUpdate(row);
  // };
  // const handleEditRow = (row: any) => {
  //   openEditModal(row);
  // };
  const submitBuildingInformation = () => {
    const result = {
      building: selectedBuilding,
      contact_name: formValues.contact_name,
      contact_phone: formValues.contact_phone,
      contact_email: formValues.contact_email,
      parking_situation: formValues.parking_situation,
      additional_information: formValues.additional_information,
      customer_information: formValues.customer_information,
    };

    return new Promise<void>((resolve, reject) => {
      api.genericApiRequest({
        entity: "buildingInformation",
        method: "put",
        entityId: buildingInformation.id,
        successHandler: () => resolve(),
        failHandler: (error: unknown) => reject(error),
        submitData: result,
      });
    });
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (savingAppointment || pendingCancellation) return;
    const result = {
      status: appointmentStatus?.value,
      datetime: date.$d.toISOString(),
      subject: subject.value,
      team: team.value,
      buildingInformation: buildingInformation.id,
      executed_by: executedBy,
      assisted_by: assistedBy,
      on_site: onSite,
    };
    await saveAppointment(result);
  };

  const saveAppointment = async (result: Record<string, unknown>) => {
    if (savingAppointment) return;
    setSavingAppointment(true);
    const isCancellation = ["canceled", "canceled_not_in_time"].includes(
      String(result.status)
    );
    const saveBuilding =
      currentAppointment && currentAppointment.appointmentslot_set.length === 0;
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    try {
      if (saveBuilding && !isCancellation) {
        await submitBuildingInformation();
      }
      // eslint-disable-next-line
      await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointment",
          method: "put",
          successHandler: () => {
            resolve();
          },
          failHandler: (error: any) => {
            reject(error);
          },
          entityId: String(appointmentId),
          submitData: result,
          config: config,
        });
      });
      if (saveBuilding && isCancellation) {
        await submitBuildingInformation();
      }
      setNotificationMessage(t("successfully_saved"));
      setNotificationVariant("success");
      setShowNotification(true);
      navigate(`/appointmentlist/`);
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "cancellation_choice_required" in error
      ) {
        setPendingCancellation(result);
      } else {
        handleError(error);
      }
    } finally {
      setSavingAppointment(false);
    }
  };
  const createNewRow = async (values: any) => {
    const existingExaminationIds = Array.isArray(assignedExaminationSet)
      ? assignedExaminationSet.map((exam: any) => exam.id)
      : [];
    const newExaminationIds = Array.isArray(values.examination)
      ? values.examination
      : [];
    const combinedIds = Array.from(
      new Set([...existingExaminationIds, ...newExaminationIds])
    );

    const result = {
      examination_set: combinedIds,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    try {
      await new Promise<any>((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointment",
          method: "put",
          entityId: String(appointmentId),
          successHandler: (res: any) => {
            resolve(res);
          },
          failHandler: (error: any) => {
            reject(error);
          },
          submitData: result,
          config: config,
        });
      });
      setModalOpen(false);
      resetTable();
    } catch (error) {
      handleError(error);
    }
  };

  const getAppointmentStatusOptions = () => {
    const additionalRouteParts: any = {};
    additionalRouteParts["available_statuses"] = "/";
    api.genericApiRequest({
      entity: "appointment",
      method: "get",
      additionalRouteParts: additionalRouteParts,
      successHandler: (res: any) => {
        const tempStatusOptionArray = res.data.results.map((item: any) =>
          getTranslatedOptionObject(item, true)
        );
        setStatusOptions(tempStatusOptionArray);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };
  // eslint-disable-next-line
  const deleteRow = useCallback(
    (row: any, skipQuestion = false) => {
      const deleteRow = async () => {
        if (!skipQuestion) {
          const isConfirmed = await showConfirmationModalAsync(
            t("confirm_delete_entry")
          );
          if (!isConfirmed) return;
        }

        const existingExaminationIds = Array.isArray(assignedExaminationSet)
          ? assignedExaminationSet.map((exam: any) => exam.id)
          : [];

        const newExaminationIds = existingExaminationIds.filter(
          (exam: any) => exam !== row.original.id
        );

        const result = { examination_set: newExaminationIds };

        const deleteExaminationRow = async () => {
          try {
            await api.genericApiRequest(
              /*eslint-enable */
              {
                entity: "appointment",
                method: "put",
                submitData: result,
                entityId: String(appointmentId),
                successHandler: () => {
                  resetTable();
                },
                failHandler: (error: any) => {
                  handleError(error);
                },
              }
            );
          } catch (error) {
            console.log(error);
          }
        };

        deleteExaminationRow();
      };
      deleteRow();
    },
    [
      assignedCategory,
      assignedExaminationSet,
      appointmentId,
      resetTable,
      showConfirmationModalAsync,
      t,
    ]
  );

  const deleteFindingRow = useCallback((row: any, skipQuestion = false) => {
    const deleteRow = async () => {
      if (!skipQuestion) {
        const isConfirmed = await showConfirmationModalAsync(
          t("confirm_delete_entry", {
            confirmButtonText: t("delete"),
          })
        );
        if (!isConfirmed) {
          return;
        }
      }
      const deleteFindingRow = async () => {
        try {
          await api.genericApiRequest(
            /*eslint-enable */
            {
              entity: "finding",
              method: "delete",
              successHandler: () => {
                resetTable();
              },
              entityId: String(row.original.id),
              failHandler: (error: any) => {
                handleError(error);
              },
            }
          );
        } catch (error) {
          console.log(error);
        }
      };
      deleteFindingRow();
    };
    deleteRow();
  }, []);

  const existingExaminationIds = useMemo(
    () =>
      Array.isArray(assignedExaminationSet)
        ? assignedExaminationSet.map((exam: any) => exam.id)
        : [],
    [assignedExaminationSet]
  );

  const appointmentCategoryOption = useMemo(
    () =>
      assignedCategory?.id
        ? [
            {
              value: assignedCategory.id,
              label: assignedCategory.name || assignedCategory.id,
            },
          ]
        : [],
    [assignedCategory?.id, assignedCategory?.name]
  );

  const additionalUrlParameters = useMemo(() => {
    const parameters: any = { include_relations: true };
    if (existingExaminationIds.length > 0) {
      parameters["id__in!"] = existingExaminationIds;
    }
    return parameters;
  }, [existingExaminationIds]);

  const columns = useMemo(
    () => [
      {
        header: t("category"),
        accessorKey: "category.name",
        id: "category",
        enableForCreate: true,
        selectOptions: appointmentCategoryOption,
        required: true,
        errorMessage: t("field_required"),
        autoFocus: assignedCategory ? false : true,
        disableColumn: assignedCategory ? true : false,
      },
      {
        header: t("examination"),
        accessorKey: "name",
        id: "examination",
        enableForCreate: true,
        autoFocus: assignedCategory ? true : false,
        filterByCategory: true,
        entity: "examination",
        asyncEntitySearch: true,
        multiselect: true,
        additionalUrlParameters: additionalUrlParameters,
        required: true,
        errorMessage: t("field_required"),
      },
    ],
    [additionalUrlParameters, appointmentCategoryOption, t]
  );

  const columnsFindings = useMemo(
    () => [
      {
        header: t("id"),
        accessorKey: "id",
        id: "id",
      },
      {
        header: t("name"),
        accessorKey: "name",
        id: "probe_name",
      },
    ],
    [assignedFindings]
  );

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;

    if (
      [
        "contact_name",
        "contact_email",
        "contact_phone",
        "parking_situation",
        "additional_information",
        "customer_information",
      ].includes(name)
    ) {
      setIsCustomBuildingInfo(true);
    }

    setFormValues((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
  };
  const handleBuildingChange = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSelectedBuilding(selectedOption.value);
    }
  };
  const handleResponsibleUserChange = (
    selectedOption: any,
    fieldName: "executed_by" | "assisted_by" | "on_site"
  ) => {
    const newValue =
      selectedOption === null
        ? null
        : selectedOption &&
          typeof selectedOption === "object" &&
          selectedOption.value
        ? selectedOption.value
        : null;

    if (newValue === null && selectedOption !== null) {
      return;
    }

    if (fieldName === "executed_by") {
      setExecutedBy(newValue);
    } else if (fieldName === "assisted_by") {
      setAssistedBy(newValue);
    } else setOnsite(newValue);
  };

  const navigateToDashboard = () =>
    navigate(`/appointmentDashboard/${appointmentId}`);

  const shouldRender = Object.values(allOptionsLoaded).every(
    (value) => value === true
  );
  if (!shouldRender) return null;

  return (
    <RestrictedPage permissionRequired="frontend_view_appointment">
      <div className={styles.formWrapper}>
        <section className={styles.headingWrapper}>
          {initialAppointmentStatus && initialAppointmentStatus !== "draft" && (
            <GenericButton
              className={styles.navigateToDashboardButton}
              onClick={navigateToDashboard}
            >
              {t("appointment_view_in_dashboard")}
            </GenericButton>
          )}
        </section>
        <form className={styles.form}>
          <DropDown
            name="appointmentStatus"
            label="Appointment Status"
            defaultValue={appointmentStatus}
            options={statusOptions}
            onChange={handleStatusChange}
            wrapperClassName={styles.dropDown__appointmentStatus}
          />
          <div className={styles.dateTimePickerWrapper}>
            <GenericDateTime
              value={date}
              className={styles.dateTimePicker}
              onChange={(newDate: any) => handleDateChange(newDate)}
              clearable={false}
              textFieldProps={{ label: t("date") }}
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.dropDownSubjectSearch}>
              <FormFieldLabel
                label={t("subject")}
                htmlFor="appointment-editor-subject"
              />
              <DropDownSubjectSearch
                name="appointment-editor-subject"
                subdomain={subdomain}
                options={subjectOptions}
                onChange={handleSubjectChange}
                preExistingValue={subject}
                disabled={appointmentStatus?.value !== "open"}
              />
            </div>
            <DropDown
              defaultValue={team}
              name="team"
              label={t("team")}
              options={teamOptions}
              onChange={handleTeamChange}
              wrapperClassName={styles.dropDown__team}
            />
            <DropDown
              defaultValue={executedBy}
              name="executed_by"
              label={t("executed_by")}
              options={executedByOptions}
              onChange={(selectedOption) =>
                handleResponsibleUserChange(selectedOption, "executed_by")
              }
              wrapperClassName={styles.dropDown__executedBy}
            />
            <DropDown
              defaultValue={assistedBy}
              name="assisted_by"
              label={t("assisted_by")}
              options={assistedByOptions}
              onChange={(selectedOption) =>
                handleResponsibleUserChange(selectedOption, "assisted_by")
              }
              isClearable={true}
              wrapperClassName={styles.dropDown__assistedBy}
            />
            <DropDown
              defaultValue={onSite}
              name="on_site"
              label={t("on_site")}
              options={onSiteOptions}
              onChange={(selectedOption) =>
                handleResponsibleUserChange(selectedOption, "on_site")
              }
              isClearable={true}
              wrapperClassName={styles.dropDown__assistedBy}
            />
            <AppointmentBuildingInformation
              appointment={currentAppointment}
              handleInputChange={handleInputChange}
              formValues={formValues}
              buildingOptions={buildingOptionsForSubject}
              selectedBuilding={selectedBuilding}
              handleBuildingChange={handleBuildingChange}
            />
          </div>
          <div>
            <MaterialReactTable
              enableHiding={true}
              enableFullScreenToggle={false}
              localization={
                logedInUserInfo.preferred_language === "en"
                  ? MRT_Localization_EN
                  : MRT_Localization_DE
              }
              state={{ isLoading: isAssignedExaminationTableLoading }}
              columns={columns}
              data={examinationTableData}
              autoResetPageIndex={false}
              enableEditing
              muiTableBodyRowProps={({ row }: any) =>
                row.index % 2 === 0
                  ? { sx: { backgroundColor: "#f5f5f5" } }
                  : { sx: { backgroundColor: "#FFF" } }
              }
              enableRowActions={true}
              renderRowActions={({ row }) => (
                <Box className={styles.tableRowActions}>
                  <Tooltip arrow placement="right" title="Delete">
                    <GenericButton
                      variant="icon"
                      color="error"
                      onClick={() => deleteRow(row)}
                    >
                      <Delete />
                    </GenericButton>
                  </Tooltip>
                </Box>
              )}
              renderTopToolbarCustomActions={() => {
                return (
                  <div
                    className={styles.tableTopToolbar}
                    style={{ flexDirection: "column" }}
                  >
                    <h3 style={{ margin: "0" }}>{t("examinations")} </h3>
                    <GenericButton onClick={() => openCreateModal()}>
                      {t("add")}
                    </GenericButton>
                  </div>
                );
              }}
              renderToolbarInternalActions={({ table }) => (
                <MrtToolbarInternalActions
                  table={table}
                  showReset={hasMrtFiltersToReset(table.getState())}
                  onReset={() => resetMrtFilters(table)}
                />
              )}
            />
          </div>
          <div className={styles.tableWrapper}>
            <MaterialReactTable
              enableHiding={true}
              enableFullScreenToggle={false}
              localization={
                logedInUserInfo.preferred_language === "en"
                  ? MRT_Localization_EN
                  : MRT_Localization_DE
              }
              state={assignedFindings}
              columns={columnsFindings}
              data={assignedFindings}
              autoResetPageIndex={false}
              muiTableBodyRowProps={({ row }: any) =>
                row.index % 2 === 0
                  ? { sx: { backgroundColor: "#f5f5f5" } }
                  : { sx: { backgroundColor: "#FFF" } }
              }
              enableRowActions={true}
              renderRowActions={({ row }) => (
                <Box className={styles.tableRowActions}>
                  <Tooltip arrow placement="right" title="Delete">
                    <GenericButton
                      variant="icon"
                      color="error"
                      onClick={() => deleteFindingRow(row)}
                    >
                      <Delete />
                    </GenericButton>
                  </Tooltip>
                </Box>
              )}
              renderTopToolbarCustomActions={() => {
                const subject = "BEATUS";
                return (
                  <div className={styles.tableTopToolbar}>
                    <h3 style={{ margin: "0" }}>
                      {t("ongoing_individual_examinations", { subject })}
                    </h3>
                  </div>
                );
              }}
              renderToolbarInternalActions={({ table }) => (
                <MrtToolbarInternalActions
                  table={table}
                  showReset={hasMrtFiltersToReset(table.getState())}
                  onReset={() => resetMrtFilters(table)}
                />
              )}
            />
          </div>
          <GenericCrudTableModal
            open={modalOpen}
            handleError={handleError}
            columns={columns}
            presetValues={modalPresetValues}
            onClose={() => setModalOpen(false)}
            limit="150"
            submitButtonText={t("add")}
            onSubmit={createNewRow}
          />
          <div className={styles.buttonWrapper}>
            <GenericButton
              variant="outlined"
              onClick={() => navigateToAppointmentlist()}
            >
              {t("back")}
            </GenericButton>
            <GenericButton
              waitingForResponse={true}
              showSpinnerOnDisable={true}
              disabled={savingAppointment || pendingCancellation !== null}
              onClick={async (event: any) => {
                await handleSubmit(event);
              }}
            >
              {t("save")}
            </GenericButton>
          </div>
        </form>
      </div>
      {errorModal}
      <AppointmentCancellationChoiceDialog
        open={pendingCancellation !== null}
        onClose={() => setPendingCancellation(null)}
        onChoose={(choice) => {
          if (!pendingCancellation || savingAppointment) return;
          const result = {
            ...pendingCancellation,
            status: choice,
            cancellation_choice: choice,
          };
          setPendingCancellation(null);
          setAppointmentStatus({ value: choice, label: t(choice) });
          void saveAppointment(result);
        }}
      />
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </RestrictedPage>
  );
}

//TODO: edit or create and examination within an appointment leads to an error if the category_set already exists.
//      MUI also states an error regarding "out of range values"
